import { Div, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'

type ProgressCircleProps = {
  percent: number
  size?: number
  description?: string
}

export const ProgressCircle = (props: ProgressCircleProps) => {
  const { percent, size = 150, description } = props

  const isGreaterThan50 = percent > 50
  const isComplete = percent === 100

  const responsiveTitle = size * 0.25
  const responsiveDescription = size * 0.09
  const ringShadow =
    'rgb(204, 219, 232) 3px 3px 6px 0px inset, rgba(255, 255, 255, 0.5) -3px -3px 6px 1px inset'
  const successGradient = 'linear-gradient(315deg, #7ee8fa 0%, #80ff72 74%)'

  const degree = (percent * 360) / 100

  const circleStyle = (size: number) => ({
    content: '""',
    position: 'absolute' as 'absolute',
    borderRadius: '50%',
    left: `calc(50% - ${size / 2}px)`,
    top: `calc(50% - ${size / 2}px)`,
    width: `${size}px`,
    height: `${size}px`,
  })

  const pieChartStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: isComplete
      ? successGradient
      : isGreaterThan50
      ? 'linear-gradient(74deg, #34E3DF 16.27%, #02AEEE 86.38%)'
      : '#E5E5E5',
    boxShadow: isGreaterThan50 ? '' : ringShadow,
    position: 'relative' as 'relative',
    margin: '50px auto 0',
  }

  const progressStyle = {
    ...circleStyle(size),
    clip: isGreaterThan50
      ? `rect(0, ${size / 2}px, ${size}px, 0)`
      : `rect(0, ${size}px, ${size}px, ${size / 2}px)`,
  }

  const progressFillStyle = {
    ...circleStyle(size),
    clip: isGreaterThan50
      ? `rect(0, ${size}px, ${size}px, ${size / 2}px)`
      : `rect(0, ${size / 2}px, ${size}px, 0)`,
    background: isComplete
      ? successGradient
      : isGreaterThan50
      ? '#E5E5E5'
      : 'linear-gradient(74deg, #34E3DF 16.27%, #02AEEE 86.38%)',
    boxShadow: isGreaterThan50 ? ringShadow : '',
    transform: `rotate(${degree}deg)`,
  }

  const percentsStyle = {
    ...circleStyle(size / 1.15),
    background: '#fff',
    textAlign: 'center' as 'center',
    display: 'table',
  }

  const percentsWrapperStyle = {
    display: 'table-cell',
    verticalAlign: 'middle' as 'middle',
  }

  const titleStyles = {
    fontSize: `${responsiveTitle}px`,
    fontWeight: 700,
    fontFamily: 'Montserrat, sans-serif',
    background: isComplete
      ? successGradient
      : 'linear-gradient(74deg, #34E3DF 16.27%, #02AEEE 86.38%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline',
    paddingLeft: '2px',
  }

  const descriptionStyles = {
    fontSize: `${responsiveDescription}px`,
    fontWeight: 700,
    fontFamily: 'Montserrat, sans-serif',
    background: isComplete
      ? successGradient
      : 'linear-gradient(74deg, #34E3DF 16.27%, #02AEEE 86.38%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }

  return (
    <Div style={pieChartStyle}>
      <Div style={progressStyle}>
        <Div style={progressFillStyle} />
      </Div>
      <Div style={percentsStyle}>
        <Div style={percentsWrapperStyle}>
          <Text
            type="body"
            content={`${percent}%`}
            alignment="center"
            style={titleStyles}
          />
          {description && (
            <Text
              type="body"
              content={description}
              alignment="center"
              style={descriptionStyles}
            />
          )}
        </Div>
      </Div>
    </Div>
  )
}
